import { inject, Injectable } from '@angular/core';
import { CameraSelectors } from '@hunting-nature/Secacam/pages/cameras/redux/cameras.reducer';
import { FilterSelectors } from '@hunting-nature/Secacam/pages/gallery/image-gallery/filter/redux/filter.reducer';
import { GeneralActions } from '@hunting-nature/Secacam/shared/redux/general/general.actions';
import type { CameraRest } from '@hunting-nature/shared-core/interfaces/camera-rest.interface';
import { NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, from, of, withLatestFrom } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { GalleryActions } from '../../redux/gallery.actions';

import type { CameraSortMode } from '../components/sort-mode-sheet/sort-mode.type';
import { FilterService } from '../services/filter.service';

import { GalleryFilterDataService } from '../services/gallery-filter-data.service';

import { FilterActions } from './filter.actions';

@Injectable()
export class FilterEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly galleryFilterDataService = inject(GalleryFilterDataService);
  private readonly navController = inject(NavController);
  private readonly filterService = inject(FilterService);

  filterOverviewPageEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.filterOverviewPageEntered),
      withLatestFrom(
        this.store.select(FilterSelectors.filterCamerasLoading),
        this.store.select(FilterSelectors.appliedCameras),
        this.store.select(CameraSelectors.getCameraDetails)
      ),
      filter(
        ([_action, filterCamerasLoading, appliedCameras, cameraDetails]) =>
          appliedCameras.length > 0 && (cameraDetails?.id === appliedCameras[0] || filterCamerasLoading)
      ),
      switchMap(([_action, _filterCamerasLoading, appliedCameras, cameraDetails]) => {
        if (cameraDetails?.id === appliedCameras[0]) {
          return of(
            FilterActions.filterOverviewPageEnteredSucceeded({
              cameras: [{ ...cameraDetails, alias: cameraDetails.name } as unknown as CameraRest],
              images: [],
            })
          );
        }

        return this.galleryFilterDataService.fetchFilterCameras$().pipe(
          map(filterCameraData => FilterActions.filterOverviewPageEnteredSucceeded(filterCameraData)),
          catchError((error: unknown) => of(FilterActions.filterOverviewPageEnteredFailed({ error })))
        );
      })
    )
  );

  camerasFilterPageEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.camerasFilterPageEntered),
      switchMap(() =>
        this.galleryFilterDataService.fetchFilterCameras$().pipe(
          map(filterCameraData => FilterActions.camerasFilterPageEnteredSucceeded(filterCameraData)),
          catchError((error: unknown) => of(FilterActions.camerasFilterPageEnteredFailed({ error })))
        )
      )
    )
  );

  loadCameraFilterSortMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.camerasFilterPageEntered),
      switchMap(() =>
        from(this.galleryFilterDataService.getCameraFilterSortMode$()).pipe(
          map((sortMode: CameraSortMode) => FilterActions.loadCameraFilterSortModeSucceeded({ sortMode })),
          catchError((error: unknown) => of(FilterActions.loadCameraFilterSortModeFailed({ error })))
        )
      )
    )
  );

  setCameraSortMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.setCameraSortMode),
      switchMap(({ sortMode }: { sortMode: CameraSortMode }) =>
        from(this.galleryFilterDataService.setCameraFilterSortMode$(sortMode)).pipe(
          map(() => FilterActions.setCameraSortModeSucceeded()),
          catchError((error: unknown) => of(FilterActions.setCameraSortModeFailed({ error })))
        )
      )
    )
  );

  loadCameraFilterShowNoMediaOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.camerasFilterPageEntered),
      switchMap(() =>
        from(this.galleryFilterDataService.getCameraFilterShowNoMediaOptions$()).pipe(
          map((show: boolean) => FilterActions.loadCameraFilterShowNoMediaOptionsSucceeded({ showNoMediaOptions: show })),
          catchError((error: unknown) => of(FilterActions.loadCameraFilterShowNoMediaOptionsFailed({ error })))
        )
      )
    )
  );

  setShowNoMediaOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.setShowNoMediaOptions),
      switchMap(({ showNoMediaOptions }: { showNoMediaOptions: boolean }) =>
        from(this.galleryFilterDataService.setCameraFilterShowNoMediaOptions$(showNoMediaOptions)).pipe(
          map(() => FilterActions.setShowNoMediaOptionsSucceeded()),
          catchError((error: unknown) => of(FilterActions.setShowNoMediaOptionsFailed({ error })))
        )
      )
    )
  );

  applyDetectionsFilter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FilterActions.applyDetectionsFilter),
        tap(() => this.navController.back())
      ),
    { dispatch: false }
  );

  applyCamerasFilter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FilterActions.applyCamerasFilter),
        tap(() => this.navController.back())
      ),
    { dispatch: false }
  );

  applyOthersFilter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FilterActions.applyOthersFilter),
        tap(() => this.navController.back())
      ),
    { dispatch: false }
  );

  applyTimeDateFilter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FilterActions.applyTimeDateFilter),
        tap(() => this.navController.back())
      ),
    { dispatch: false }
  );

  applyCustomAlbumFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.applyCustomAlbumFilter),
      switchMap(() => of(GalleryActions.clearFilteredImages()))
    )
  );

  loadAvailableTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.goToGalleryFilter),
      switchMap(() =>
        this.filterService.fetchAllDetectionTags$().pipe(
          map((tags: Array<string>) => FilterActions.loadAvailableTagsSucceeded({ tags })),
          catchError((error: unknown) => {
            console.error('Failed to load available tags', error);
            return of(FilterActions.loadAvailableTagsFailed({ error }));
          })
        )
      )
    )
  );

  reloadAvailableTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.filterDetectionPageEntered),
      withLatestFrom(this.store.select(FilterSelectors.availableTagsLoaded)),
      filter(([_action, availableTagsLoaded]) => !availableTagsLoaded),
      switchMap(() =>
        this.filterService.fetchAllDetectionTags$().pipe(
          map((tags: Array<string>) => FilterActions.loadAvailableTagsSucceeded({ tags })),
          catchError((error: unknown) => {
            console.error('Failed to load available tags', error);
            return of(FilterActions.loadAvailableTagsFailed({ error }));
          })
        )
      )
    )
  );

  // TODO: refactor redux flow - simplify effect - no effect chains
  triggerGalleryRefreshAfterFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        FilterActions.applyDetectionsFilter,
        FilterActions.applyCamerasFilter,
        FilterActions.applyOthersFilter,
        FilterActions.applyTimeDateFilter,
        FilterActions.applyCustomAlbumFilter,
        FilterActions.clearAll,
        FilterActions.clearAllPreserveCameraLock,
        FilterActions.clearDetections,
        FilterActions.clearCameras,
        FilterActions.clearPeriod,
        FilterActions.clearOthers,
        FilterActions.clearTimeDateFilter
      ),
      switchMap(() => of(GalleryActions.clearFilteredImages()))
    )
  );
}
